import { ONE_DRIVE_AZURE_CLIENT_ID } from '../../../../constants/Constants';
import { DOCUMENT_TOAST } from '../../../../constants/toastConstant';
import Toast from '../../utils/Toast';

const getEmailFromGraphAPI = async (accessToken) => {
  try {
    const response = await fetch('https://graph.microsoft.com/v1.0/me', {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch user information from Graph API');
    }

    const data = await response.json();
    return data.mail || data.userPrincipalName; // Fallback if mail is not available
  } catch (error) {
    console.error('Error fetching email:', error);
    return null;
  }
};

const oneDrivePicker = async (accessToken) => {
  try {
    const email = await getEmailFromGraphAPI(accessToken);

    const odOptions = {
      clientId: ONE_DRIVE_AZURE_CLIENT_ID,
      action: 'share',
      multiSelect: true,
      advanced: {
        accessToken,
        filter: '.jpg,.jpeg,.png,.pdf',
      },
      success: (files) => {
        const uploadEventFiles = files?.value || [];
        const allowedFileTypes = [
          'image/jpeg',
          'image/jpg',
          'image/png',
          'application/pdf',
        ];
        const maxSizeInBytes = 10 * 1024 * 1024; // max size allowed 10mb

        // Filter valid files
        const filesFormatValid = uploadEventFiles.filter((file) =>
          allowedFileTypes.includes(file?.file?.mimeType),
        );

        // Show errors for invalid file types
        uploadEventFiles
          .filter((file) => !allowedFileTypes.includes(file?.file?.mimeType))
          .forEach((file) => {
            Toast({
              message: `"${file.name}" ${DOCUMENT_TOAST.FORMAT_INVALID}`,
              variant: 'error',
              position: { vertical: 'top', horizontal: 'center' },
              autoHideDuration: 4000,
            });
          });

        const sizeLimitFilesValid = filesFormatValid?.filter(
          (file) => file.size <= maxSizeInBytes,
        );
        filesFormatValid
          ?.filter((file) => file.size > maxSizeInBytes)
          ?.forEach((file) => {
            Toast({
              message: `"${file.name}" ${DOCUMENT_TOAST.FILE_SIZE_TOO_LARGE}`,
              variant: 'error',
              position: { vertical: 'top', horizontal: 'center' },
              autoHideDuration: 4000,
            });
          });

        // Validate filenames for spaces or special characters
        const fileNamesWithoutSpacesOrSpecialChars = sizeLimitFilesValid.filter(
          (file) => /^[^\s!@#$%^&*]+$/.test(file.name),
        );

        // Show errors for filenames with invalid characters
        filesFormatValid
          .filter((file) => /[ !@#$%^&*]/.test(file.name))
          .forEach((file) => {
            Toast({
              message: `"${file.name}" ${DOCUMENT_TOAST.SPACES_OR_SPECIAL_CHARS_NOT_ALLOWED}`,
              variant: 'error',
              position: { vertical: 'top', horizontal: 'center' },
              autoHideDuration: 4000,
            });
          });

        const filesWithDownloadUrls = fileNamesWithoutSpacesOrSpecialChars.map(
          (file) => ({
            name: file.name,
            mimeType: file.file.mimeType,
            downloadUrl: file['@microsoft.graph.downloadUrl'],
          }),
        );

        return {
          files: filesWithDownloadUrls,
          accessToken,
          folder: uploadEventFiles[0]?.parentReference?.name,
          email,
        };
      },
      error: (e) => {
        console.error('OneDrive Error:', e);
        throw new Error('Error occurred while selecting files');
      },
    };

    // Open OneDrive picker popup
    return await new Promise((resolve, reject) => {
      window?.OneDrive?.open({
        ...odOptions,
        success: (files) => {
          const result = odOptions.success(files);
          resolve(result);
        },
        error: (e) => {
          odOptions.error(e);
          reject(e);
        },
      });
    });
  } catch (error) {
    console.error('Error from OneDrive while file selection:', error);
    return { error: 'OneDrive file selection failed' };
  }
};

export const outlook = async ({ instance }) => {
  try {
    const authResponse = await instance.loginPopup({
      scopes: [
        'User.Read',
        'Files.Read',
        'Files.Read.All',
        'Files.Read.Selected',
      ],
      prompt: 'select_account',
    });

    const { accessToken } = authResponse;

    return { accessToken };
  } catch (error) {
    console.error('Error from OneDrive while file selection:', error);
    return 'Error: OneDrive file selection failed';
  }
};
export default oneDrivePicker;
